/**
 * 传奇作品详情页
 *
 * 展示单个传奇作品的完整信息
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { SaluteButton } from '../components/legend';
import { BadgeWall } from '../components/rider';
import * as legendApi from '../services/legendApi';
import styles from './LegendDetailPage.module.css';

export default function LegendDetailPage() {
    const { legendId } = useParams();
    const navigate = useNavigate();
    const { language } = useLanguage();
    const { user, isLoggedIn } = useAuth();

    const [legend, setLegend] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showShareMenu, setShowShareMenu] = useState(false);

    const isOwner = isLoggedIn && legend?.rider?.id === user?.id;

    useEffect(() => {
        loadLegend();
    }, [legendId]);

    const loadLegend = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await legendApi.getLegend(legendId);
            if (result.success) {
                setLegend(result.data);
            } else {
                setError(result.error || 'Failed to load legend');
            }
        } catch (err) {
            console.error('Failed to load legend:', err);
            setError('Failed to load legend');
        } finally {
            setLoading(false);
        }
    };

    const handleSalute = (newCount, isSaluted) => {
        setLegend(prev => ({
            ...prev,
            salutes_count: newCount,
            is_saluted: isSaluted
        }));
    };

    const handleShare = async (channel) => {
        setShowShareMenu(false);
        try {
            await legendApi.trackShare(legendId, channel);

            if (channel === 'copy') {
                const url = `${window.location.origin}/legend/${legendId}`;
                await navigator.clipboard.writeText(url);
                alert('链接已复制到剪贴板');
            } else if (channel === 'poster') {
                // TODO: Open poster generator
                navigate(`/legend/${legendId}/poster`);
            }
        } catch (err) {
            console.error('Share failed:', err);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('确定要删除这个传奇作品吗？此操作不可撤销。')) {
            return;
        }

        try {
            const result = await legendApi.deleteLegend(legendId);
            if (result.success) {
                navigate('/legends');
            }
        } catch (err) {
            console.error('Delete failed:', err);
        }
    };

    const getLevelInfo = () => {
        if (!legend?.rider) return null;
        return legendApi.getRiderLevelInfo(legend.rider.level || 1, language);
    };

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <p>加载中...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <div className={styles.error}>
                    <span className={styles.errorIcon}>!</span>
                    <h2>无法加载传奇作品</h2>
                    <p>{error}</p>
                    <Link to="/legends" className={styles.backBtn}>
                        返回传奇画廊
                    </Link>
                </div>
            </div>
        );
    }

    const levelInfo = getLevelInfo();

    return (
        <div className={styles.page}>
            {/* 主图区域 */}
            <div className={styles.heroSection}>
                <div className={styles.imageContainer}>
                    {legend.cover_image_url ? (
                        <img
                            src={legend.cover_image_url}
                            alt={legend.title}
                            className={styles.heroImage}
                        />
                    ) : (
                        <div className={styles.imagePlaceholder}>
                            <span></span>
                        </div>
                    )}

                    {/* 徽章 */}
                    {legend.is_featured && (
                        <span className={styles.featuredBadge}>
                            <span></span> 编辑精选
                        </span>
                    )}
                    {legend.is_hall_of_fame && (
                        <span className={styles.hofBadge}>
                            <span></span> 名人堂
                        </span>
                    )}
                </div>
            </div>

            {/* 内容区域 */}
            <div className={styles.content}>
                {/* 标题和操作 */}
                <div className={styles.titleSection}>
                    <div className={styles.titleInfo}>
                        <h1 className={styles.title}>{legend.title}</h1>
                        <span className={styles.serialNumber}>
                            {legendApi.formatSerialNumber(legend.id)}
                        </span>
                    </div>

                    <div className={styles.actions}>
                        <SaluteButton
                            legendId={legend.id}
                            initialCount={legend.salutes_count || 0}
                            initialSaluted={legend.is_saluted}
                            size="large"
                            showText={true}
                            onSalute={handleSalute}
                        />

                        <div className={styles.shareDropdown}>
                            <button
                                className={styles.shareBtn}
                                onClick={() => setShowShareMenu(!showShareMenu)}
                            >
                                <span></span> 分享
                            </button>
                            {showShareMenu && (
                                <div className={styles.shareMenu}>
                                    <button onClick={() => handleShare('poster')}>
                                        <span></span> 生成海报
                                    </button>
                                    <button onClick={() => handleShare('copy')}>
                                        <span></span> 复制链接
                                    </button>
                                </div>
                            )}
                        </div>

                        {isOwner && (
                            <button
                                className={styles.deleteBtn}
                                onClick={handleDelete}
                            >
                                删除
                            </button>
                        )}
                    </div>
                </div>

                {/* 描述 */}
                {legend.description && (
                    <p className={styles.description}>{legend.description}</p>
                )}

                {/* 统计 */}
                <div className={styles.stats}>
                    <div className={styles.statItem}>
                        <span className={styles.statIcon}></span>
                        <span className={styles.statValue}>
                            {legendApi.formatNumber(legend.salutes_count || 0)}
                        </span>
                        <span className={styles.statLabel}>致敬</span>
                    </div>
                    <div className={styles.statItem}>
                        <span className={styles.statIcon}></span>
                        <span className={styles.statValue}>
                            {legendApi.formatNumber(legend.views_count || 0)}
                        </span>
                        <span className={styles.statLabel}>浏览</span>
                    </div>
                    <div className={styles.statItem}>
                        <span className={styles.statIcon}></span>
                        <span className={styles.statValue}>
                            {legendApi.formatNumber(legend.shares_count || 0)}
                        </span>
                        <span className={styles.statLabel}>分享</span>
                    </div>
                </div>

                {/* 骑士信息 */}
                <Link to={`/rider/${legend.rider?.id}`} className={styles.riderCard}>
                    <div className={styles.riderAvatar}>
                        {legend.rider?.avatar_url ? (
                            <img src={legend.rider.avatar_url} alt={legend.rider.nickname} />
                        ) : (
                            <span className={styles.avatarFallback}>
                                {legend.rider?.nickname?.charAt(0)?.toUpperCase() || 'R'}
                            </span>
                        )}
                    </div>
                    <div className={styles.riderInfo}>
                        <span className={styles.riderName}>
                            {legend.rider?.nickname || '匿名骑士'}
                            {legend.rider?.is_verified && (
                                <span className={styles.verifiedIcon}></span>
                            )}
                        </span>
                        <span className={styles.riderLevel}>
                            {levelInfo?.icon} Lv.{legend.rider?.level || 1} {levelInfo?.title}
                        </span>
                    </div>
                    <BadgeWall
                        badges={legend.rider?.badges || []}
                        maxDisplay={3}
                        size="small"
                    />
                    <span className={styles.riderArrow}></span>
                </Link>

                {/* 配置信息 */}
                {legend.configuration && (
                    <div className={styles.configSection}>
                        <h3 className={styles.sectionTitle}>配置详情</h3>
                        <Link
                            to={`/gallery/${legend.configuration.id}`}
                            className={styles.configCard}
                        >
                            <div className={styles.configThumb}>
                                {legend.configuration.cover_image_url ? (
                                    <img
                                        src={legend.configuration.cover_image_url}
                                        alt={legend.configuration.title}
                                    />
                                ) : (
                                    <span></span>
                                )}
                            </div>
                            <div className={styles.configInfo}>
                                <span className={styles.configTitle}>
                                    {legend.configuration.title}
                                </span>
                                <span className={styles.configPrice}>
                                    ¥{legend.configuration.total_price?.toLocaleString()}
                                </span>
                            </div>
                            <span className={styles.configArrow}>查看详情 →</span>
                        </Link>
                    </div>
                )}

                {/* 铸造时间 */}
                <div className={styles.meta}>
                    <span className={styles.forgedAt}>
                        铸造于 {new Date(legend.created_at).toLocaleDateString('zh-CN', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        })}
                    </span>
                </div>
            </div>
        </div>
    );
}
